import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { AI_PROVIDERS, type AiSettings } from "@mycompbox/shared";
import { EmptyState } from "@/components/new-look/blocks";
import { MERGE_POLICY_OPTIONS } from "@/components/teams/merge-policy-card";
import { TeamForm } from "@/components/teams/team-form";
import { WriteGuard } from "@/components/billing/write-guard";
import { useAiSettings } from "@/hooks/queries/use-ai-settings";
import { useTeamMembers, useTeamTree } from "@/hooks/queries/use-teams";
import { useTeamRollup } from "@/hooks/queries/use-team-rollup";
import { usePageTitle } from "@/hooks/use-page-title";
import { getInitials } from "@/lib/get-initials";
import type { MergePolicy, TeamTreeNode } from "@/lib/api/types";

/**
 * The new look's Teams page (docs/new-look/PLAN.md, step 7.1): each top-level
 * team with its people, its projects, how merges work, which AI key runs use
 * and its people's machines. Same data as today; changes stay on the team's
 * own page.
 */

/** A team's projects: the teams under it that hold work, or the team itself when it has none. */
export function projectsOf(team: TeamTreeNode): TeamTreeNode[] {
  if (team.children.length === 0) return team.canOwnEpics === false ? [] : [team];
  return team.children.flatMap(projectsOf);
}

/** One merge rule for the team, or "Differs by project" when its projects disagree. */
export function mergeRule(projects: readonly TeamTreeNode[]): string {
  const policies = new Set<MergePolicy>(projects.map((p) => p.mergePolicy ?? "never"));
  if (policies.size > 1) return "Differs by project";
  const only = [...policies][0] ?? "never";
  return MERGE_POLICY_OPTIONS.find((o) => o.value === only)?.label ?? "Never";
}

/** Which key runs use, in words. */
export function keyText(ai: AiSettings | undefined): string {
  if (!ai) return "—";
  if (ai.effectiveKey === "personal" && ai.personalKey) {
    return `Your own key · ${AI_PROVIDERS[ai.personalKey.provider].name}`;
  }
  if (ai.effectiveKey === "company" && ai.companyKey) {
    return `Company key · ${AI_PROVIDERS[ai.companyKey.provider].name}`;
  }
  return "None yet (runs use your computer's sign-in)";
}

const MAX_FACES = 6;
const MAX_NAMES = 3;

function names(list: readonly string[]): string {
  if (list.length === 0) return "None";
  const shown = list.slice(0, MAX_NAMES).join(", ");
  return list.length > MAX_NAMES ? `${shown} +${String(list.length - MAX_NAMES)}` : shown;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-border py-1.5 text-sm last:border-b-0">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium text-foreground">{children}</dd>
    </div>
  );
}

function TeamCard({ team, keyLine }: { team: TeamTreeNode; keyLine: string }) {
  const { data: members = [], isLoading } = useTeamMembers(team.id);
  const { data: rollup } = useTeamRollup(team.id);
  const projects = projectsOf(team);
  const machines = [
    ...new Set((rollup?.members ?? []).flatMap((m) => (m.machines ?? []).map((x) => x.name))),
  ];
  const faces = members.slice(0, MAX_FACES);

  return (
    <section
      aria-label={team.name}
      className="flex min-w-0 flex-col gap-3 rounded-2xl border border-border bg-background p-4 sm:p-5"
    >
      <div className="flex items-center gap-2.5">
        {team.key && (
          <span
            className="rounded-md px-2 py-0.5 text-xs font-bold text-white"
            style={{ backgroundColor: team.color ?? "#2563eb" }}
          >
            {team.key}
          </span>
        )}
        <h2 className="min-w-0 truncate text-lg font-semibold text-foreground">{team.name}</h2>
      </div>

      <div className="flex min-h-8 items-center" aria-label={`${String(members.length)} people`}>
        {isLoading ? (
          <span className="h-8 w-24 animate-pulse rounded-full bg-muted" />
        ) : members.length === 0 ? (
          <span className="text-sm text-muted-foreground">No one yet</span>
        ) : (
          <>
            {faces.map((m) => (
              <span
                key={m.id}
                title={m.user.name}
                className="-ml-1.5 flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-muted text-[11px] font-bold text-foreground first:ml-0"
              >
                {getInitials(m.user.name)}
              </span>
            ))}
            {members.length > MAX_FACES && (
              <span className="ml-2 text-sm text-muted-foreground">
                +{members.length - MAX_FACES}
              </span>
            )}
          </>
        )}
      </div>

      <dl>
        <Fact label="Projects">{names(projects.map((p) => p.name))}</Fact>
        <Fact label="Merges">{projects.length > 0 ? mergeRule(projects) : "—"}</Fact>
        <Fact label="AI key">{keyLine}</Fact>
        <Fact label="Machines">{rollup ? names(machines) : "—"}</Fact>
      </dl>

      <Link
        to={`/teams/${team.id}`}
        className="mt-auto inline-flex min-h-11 items-center justify-center rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted"
      >
        Open the team
      </Link>
    </section>
  );
}

export function AllTeamsPage() {
  usePageTitle("Teams");
  const [creating, setCreating] = useState(false);
  const { data: tree = [], isLoading, isError } = useTeamTree();
  const { data: ai } = useAiSettings();
  const keyLine = keyText(ai);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Teams</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Who is in each team, how merges work, and which AI key they use.
          </p>
        </div>
        <WriteGuard>
          <button
            type="button"
            onClick={() => {
              setCreating(true);
            }}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-semibold text-foreground hover:bg-muted"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New team
          </button>
        </WriteGuard>
      </div>

      {isLoading ? (
        <div
          aria-busy="true"
          aria-label="Loading teams"
          className="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-72 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          title="Couldn't load teams"
          body="Check your connection and refresh the page."
        />
      ) : tree.length === 0 ? (
        <EmptyState title="No teams yet" body="Make a team to start adding projects and people." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tree.map((team) => (
            <TeamCard key={team.id} team={team} keyLine={keyLine} />
          ))}
        </div>
      )}

      <TeamForm open={creating} onOpenChange={setCreating} />
    </div>
  );
}
